import type { TaskListQuery } from "@common/contracts/task.contract";
import type { TaskStatus } from "@common/types/task";
import { buildTaskListQuery } from "../buildTaskListQuery";
import { DEFAULT_TASK_LIST_QUERY } from "../taskListConstants";

/** What Clear filters empties besides the filter menu; statuses go back to the page's defaults. */
export const CLEARED_TASK_FILTER_FIELDS = {
    search: "",
    start: undefined,
    end: undefined,
    entityIds: undefined,
} satisfies TaskListQuery;

type TaskFilterValue = TaskListQuery[keyof TaskListQuery];

/** An empty search or list counts as no filter; lists compare by their values in any order. */
const toComparable = (value: TaskFilterValue) => {
    if (Array.isArray(value)) {
        return value.length > 0 ? [...value].sort().join(",") : undefined;
    }
    return value === "" ? undefined : value;
};

interface TaskFiltersArgs {
    filters: TaskListQuery;
    /** The statuses Clear filters goes back to; undefined shows every status. */
    defaultStatuses: TaskStatus[] | undefined;
}

/** Whether `statuses` is every status or a set a Tasks page opens with. */
const isOpeningStatuses = ({
    filters: { status },
    defaultStatuses,
}: TaskFiltersArgs) =>
    [undefined, defaultStatuses, DEFAULT_TASK_LIST_QUERY.status].some(
        (openingStatuses) =>
            toComparable(openingStatuses) === toComparable(status),
    );

/**
 * Whether any filter narrows the tasks: anything Clear filters would empty,
 * or statuses other than the ones the page opens with. Grouping and sort
 * aren't filters.
 */
export const hasActiveTaskFilters = (args: TaskFiltersArgs) => {
    const { filters } = args;
    const cleared = buildTaskListQuery({
        filters: { ...filters, ...CLEARED_TASK_FILTER_FIELDS },
        overlayFilters: {},
    });
    const valuesByKey = new Map(Object.entries(filters));
    return (
        !isOpeningStatuses(args) ||
        Object.entries(cleared).some(
            ([key, value]) =>
                toComparable(valuesByKey.get(key)) !== toComparable(value),
        )
    );
};
